import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

export function SelectionDock({ rows, onClear, onActivate, busy }) {
  const [confirming, setConfirming] = useState(false);
  const n = rows.length;
  const shown = rows.slice(0, 3);

  return (
    <>
      <AnimatePresence>
        {n > 0 && !confirming && (
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 40, scale: 0.97 }}
                      transition={{ type: "spring", stiffness: 320, damping: 28 }}
                      data-testid="vcb-selection-dock"
                      className="fixed bottom-6 left-4 right-[120px] md:left-[272px] z-[90]">
            <div className="relative rounded-sm border border-[#D8B23E]/60 bg-[#0A0906]/90 backdrop-blur-2xl shadow-[0_30px_80px_-24px_rgba(216,178,62,0.45)] px-5 py-4 flex items-center gap-4 flex-wrap">
              <motion.span aria-hidden
                           animate={{ opacity: [0.25, 0.7, 0.25] }}
                           transition={{ duration: 2.4, repeat: Infinity }}
                           className="absolute -inset-px rounded-sm pointer-events-none"
                           style={{ boxShadow: "0 0 0 1px rgba(216,178,62,0.45), 0 0 32px -8px rgba(216,178,62,0.6)" }} />
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-sm bg-[#D8B23E] text-black font-serif text-xl inline-flex items-center justify-center shrink-0"
                     data-testid="vcb-selection-count">{n}</div>
                <div className="min-w-0">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
                    Precision Selection · fee-waived induction
                  </div>
                  <div className="text-[12px] text-[#C9C9C2] font-body truncate">
                    {shown.map((r) => r.email).join(" · ")}{n > 3 ? ` · +${n - 3} more` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={onClear} disabled={busy}
                        data-testid="vcb-selection-clear"
                        className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.22em] text-[9px]">
                  <X size={11} className="mr-1" /> Clear
                </Button>
                <Button size="sm" onClick={() => setConfirming(true)} disabled={busy}
                        data-testid="vcb-activate-selected"
                        className="bg-[#D8B23E] text-black hover:bg-[#E5C450] font-grotesk uppercase tracking-[0.24em] text-[9px] px-5 shadow-[0_0_22px_-4px_rgba(216,178,62,0.8)]">
                  {busy ? <Loader2 size={11} className="animate-spin" />
                        : <><Sparkles size={11} className="mr-1.5" /> Activate {n} Selected</>}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent data-testid="vcb-selection-confirm"
                       className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#D8B23E]" /> Induct {n} member{n === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
              Each selected member is inducted fee-waived with lifetime privilege and sees VCB in their dashboard immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-y-auto rounded-sm border border-white/10 bg-[#0A0906] divide-y divide-white/5">
            {rows.map((r) => (
              <div key={r.id} className="px-3 py-2 flex items-center justify-between text-[12px] font-body">
                <span className="text-[#F5F5F0] truncate">{r.email}</span>
                <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] shrink-0 ml-3">{r.role}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)}
                    className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.24em] text-[10px]">
              Cancel
            </Button>
            <Button data-testid="vcb-selection-confirm-btn"
                    onClick={() => { setConfirming(false); onActivate(); }}
                    className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
              <Sparkles size={11} className="mr-1" /> Fire Precision Pulse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
